import { isEmpty, isNil } from 'lodash';
import { buildCardField } from '@shared/data/cards/buildCardField';
import { Field } from '@shared/data/entity/fields/Field';
import { applyComponentOverrides } from '@shared/scenes/entity/util/useViewComponents';
import { isInputComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { SubtableRowRefs } from './useSubtableRows';

const EMPTY_REFS: SubtableRowRefs = {};

/**
 * The subtable's visible columns in builder order: its input components, minus the
 * server-managed pointer columns and anything hidden. The table tracks the Update view template
 * (the one the row form edits with): a field it hides is hidden as a column, and its other
 * overrides (a label) carry over. Without one, the template's own components.
 */
export function getSubtableColumns(
  workflowTemplate: API.WorkflowTemplate | undefined,
  updateViewTemplate?: API.ViewTemplateFragment,
): API.AnyComponent[] {
  const components = applyComponentOverrides(
    workflowTemplate?.components ?? [],
    updateViewTemplate,
  );
  return components.filter(
    component =>
      isInputComponent(component) &&
      !('isServerManaged' in component && component.isServerManaged) &&
      !('isHidden' in component && component.isHidden),
  );
}

export function getSubtableCellField(
  row: API.WorkflowEntityFragment,
  column: API.AnyComponent,
  refs: SubtableRowRefs | undefined,
): Field | null {
  return buildCardField({ entity: row, component: column, refs: refs ?? EMPTY_REFS });
}

/**
 * A row's ephemeral number: its 1-based position in the loaded rows (sorted oldest first, and
 * paged by appending, so it holds while more load). Nothing stores it, so rows renumber when one is
 * added or deleted.
 */
export function getSubtableRowNumber(index: number): number {
  return index + 1;
}

/**
 * The three text slots a row collapses to on mobile: the first column titles the row, the
 * middle columns join into a secondary line, and the last column is the trailing value.
 */
export function getSubtableRowSlots(
  row: API.WorkflowEntityFragment,
  columns: API.AnyComponent[],
  refs: SubtableRowRefs | undefined,
): { title: string; secondary: string; trailing: string } {
  const values = columns.map(column => displayValue(getSubtableCellField(row, column, refs)));
  const [title = '', ...rest] = values;
  const trailing = rest.pop() ?? '';
  const secondary = rest.filter(value => !isEmpty(value)).join(' · ');
  return { title, secondary, trailing };
}

function displayValue(field: Field | null): string {
  if (isNil(field) || field.isEmpty) {
    return '';
  }
  return field.toDisplayString();
}
